import { useState } from "react";
import {
    useTriggerOsmImport,
    useTriggerLantmaterietImport,
    useTriggerElectoralDistrictImport,
    useTriggerPostalCodeImport,
    type OsmImportRequest,
    type LantmaterietImportRequest,
    type ElectoralDistrictImportRequest,
    type PostalCodeImportRequest,
} from "../api/imports";
import { showSuccess, showError } from "../shared/toast";
import "./imports-panel.css";

export const ImportsPanel = (): JSX.Element => {
    const [osmNotes, setOsmNotes] = useState("");
    const [lmNotes, setLmNotes] = useState("");
    const [edNotes, setEdNotes] = useState("");
    const [pcNotes, setPcNotes] = useState("");

    const osmMutation = useTriggerOsmImport();
    const lmMutation = useTriggerLantmaterietImport();
    const edMutation = useTriggerElectoralDistrictImport();
    const pcMutation = useTriggerPostalCodeImport();

    const handleOsmImport = async () => {
        try {
            const request: OsmImportRequest = {
                notes: osmNotes || undefined,
            };
            const result = await osmMutation.mutateAsync(request);
            showSuccess(`OSM import startad. Job ID: ${result.jobId}`);
            setOsmNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte starta OSM import";
            showError("Fel", message);
        }
    };

    const handleLmImport = async () => {
        try {
            const request: LantmaterietImportRequest = {
                notes: lmNotes || undefined,
            };
            const result = await lmMutation.mutateAsync(request);
            showSuccess(`Lantmäteriet import startad. Job ID: ${result.jobId}`);
            setLmNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte starta Lantmäteriet import";
            showError("Fel", message);
        }
    };

    const handleEdImport = async () => {
        try {
            const request: ElectoralDistrictImportRequest = {
                notes: edNotes || undefined,
            };
            const result = await edMutation.mutateAsync(request);
            showSuccess(`Valdistrikt import startad. Job ID: ${result.jobId}`);
            setEdNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte starta valdistrikt import";
            showError("Fel", message);
        }
    };

    const handlePcImport = async () => {
        try {
            const request: PostalCodeImportRequest = {
                notes: pcNotes || undefined,
            };
            const result = await pcMutation.mutateAsync(request);
            showSuccess(`Postnummer import startad. Job ID: ${result.jobId}`);
            setPcNotes("");
        } catch (error: any) {
            const message = error?.response?.data?.message || "Kunde inte starta postnummer import";
            showError("Fel", message);
        }
    };

    const openHangfireDashboard = () => {
        window.open("/hangfire", "_blank");
    };

    return (
        <section className="imports-panel" aria-label="Import-jobb">
            <div className="imports-panel__header">
                <h2>Import-jobb</h2>
                <div className="imports-panel__actions">
                    <button
                        className="imports-panel__hangfire-button"
                        onClick={openHangfireDashboard}
                        title="Öppna Hangfire Dashboard för att se jobb-status"
                    >
                        Öppna Hangfire Dashboard
                    </button>
                </div>
            </div>

            <div className="imports-panel__info">
                <p>
                    Använd dessa knappar för att trigga import-jobb. Jobben körs i bakgrunden via Hangfire.
                    Du kan följa status i Hangfire Dashboard.
                </p>
            </div>

            <div className="imports-panel__jobs">
                {/* OSM Import */}
                <div className="import-job-card">
                    <div className="import-job-card__header">
                        <h3>OSM Import</h3>
                        <span className="import-job-card__badge">Vägar & Adresser</span>
                    </div>
                    <p className="import-job-card__description">
                        Importerar vägar och adresser från OpenStreetMap för hela Sverige (eller område angivet i OsmImport:DefaultBoundingBox).
                    </p>
                    <div className="import-job-card__form">
                        <label htmlFor="osm-notes">Anteckningar (valfritt):</label>
                        <textarea
                            id="osm-notes"
                            value={osmNotes}
                            onChange={(e) => setOsmNotes(e.target.value)}
                            rows={2}
                            placeholder="Lägg till anteckningar om importen..."
                            className="import-job-card__notes"
                        />
                        <button
                            className="import-job-card__trigger-button"
                            onClick={handleOsmImport}
                            disabled={osmMutation.isPending}
                        >
                            {osmMutation.isPending ? "Startar..." : "Starta OSM Import"}
                        </button>
                    </div>
                </div>

                {/* Lantmäteriet Import */}
                <div className="import-job-card">
                    <div className="import-job-card__header">
                        <h3>Lantmäteriet Import</h3>
                        <span className="import-job-card__badge">Adresser</span>
                    </div>
                    <p className="import-job-card__description">
                        Importerar adresser från Lantmäteriet baserat på fil-sökväg.
                    </p>
                    <div className="import-job-card__instructions" role="region" aria-label="Innan du startar Lantmäteriet-import">
                        <strong>Innan du startar jobbet:</strong>
                        <ol>
                            <li>
                                Hämta <strong>Consumer Key</strong> och <strong>Consumer Secret</strong> (så heter de på LM:s sida) från Lantmäteriets devportal:{" "}
                                <a
                                    href="https://apimanager.lantmateriet.se/devportal/applications/ebb249c5-cd78-4747-8ca3-4d6f4db85531/productionkeys/oauth"
                                    target="_blank"
                                    rel="noopener noreferrer"
                                >
                                    Öppna Lantmäteriet OAuth-nycklar
                                </a>
                            </li>
                            <li>
                                Ladda ned data med <code>scripts/Download-LantmaterietData.ps1</code> (en kommun per körning; använd <code>-MunicipalityCode</code> t.ex. 0126 för Huddinge). För flera kommuner, kör skriptet flera gånger eller slå ihop .gpkg-filer.
                            </li>
                            <li>
                                För <strong>alla</strong> filer i Data-mappen: sätt <code>Lantmateriet:GeoPackageDirectory</code> till <code>Data</code> i appsettings. För <strong>en</strong> fil: sätt <code>Lantmateriet:GeoPackagePath</code> till den .gpkg-fil som ska importeras.
                            </li>
                        </ol>
                        <p className="import-job-card__instructions-note">Detaljer: se Documentation/CenterReach-Import-Runbook.md, avsnitt 1.4.</p>
                    </div>
                    <div className="import-job-card__form">
                        <label htmlFor="lm-notes">Anteckningar (valfritt):</label>
                        <textarea
                            id="lm-notes"
                            value={lmNotes}
                            onChange={(e) => setLmNotes(e.target.value)}
                            rows={2}
                            placeholder="Lägg till anteckningar om importen..."
                            className="import-job-card__notes"
                        />
                        <button
                            className="import-job-card__trigger-button"
                            onClick={handleLmImport}
                            disabled={lmMutation.isPending}
                        >
                            {lmMutation.isPending ? "Startar..." : "Starta Lantmäteriet Import"}
                        </button>
                    </div>
                </div>

                {/* Electoral District Import */}
                <div className="import-job-card">
                    <div className="import-job-card__header">
                        <h3>Valdistrikt Import</h3>
                        <span className="import-job-card__badge">Valdistrikt</span>
                    </div>
                    <p className="import-job-card__description">
                        Importerar valdistrikt från fil.
                    </p>
                    <div className="import-job-card__form">
                        <label htmlFor="ed-notes">Anteckningar (valfritt):</label>
                        <textarea
                            id="ed-notes"
                            value={edNotes}
                            onChange={(e) => setEdNotes(e.target.value)}
                            rows={2}
                            placeholder="Lägg till anteckningar om importen..."
                            className="import-job-card__notes"
                        />
                        <button
                            className="import-job-card__trigger-button"
                            onClick={handleEdImport}
                            disabled={edMutation.isPending}
                        >
                            {edMutation.isPending ? "Startar..." : "Starta Valdistrikt Import"}
                        </button>
                    </div>
                </div>

                {/* Postal Code Import */}
                <div className="import-job-card">
                    <div className="import-job-card__header">
                        <h3>Postnummer Import</h3>
                        <span className="import-job-card__badge">Postnummer</span>
                    </div>
                    <p className="import-job-card__description">
                        Importerar postnummer från fil.
                    </p>
                    <div className="import-job-card__form">
                        <label htmlFor="pc-notes">Anteckningar (valfritt):</label>
                        <textarea
                            id="pc-notes"
                            value={pcNotes}
                            onChange={(e) => setPcNotes(e.target.value)}
                            rows={2}
                            placeholder="Lägg till anteckningar om importen..."
                            className="import-job-card__notes"
                        />
                        <button
                            className="import-job-card__trigger-button"
                            onClick={handlePcImport}
                            disabled={pcMutation.isPending}
                        >
                            {pcMutation.isPending ? "Startar..." : "Starta Postnummer Import"}
                        </button>
                    </div>
                </div>
            </div>
        </section>
    );
};